import { FaberApi, OrbitApi } from 'api'
import { AxiosError, AxiosPromise, AxiosResponse } from 'axios'
import {
  CreateUserPlanResponse,
  EditUserPlanDetailsResponse,
  GetActiveUserPlansType,
  GetAllFaberUsers,
  GetAllOtpackUserPlansType,
  GetOtpackHistoryType,
  GetPlansDetailsType,
  GetSubscriptionUserPlansType,
  GetUserPlansType,
  UserPlanByPaymentIdType
} from 'types/api/faber/oneTimePack.type'

type RevampPlanRow = {
  id?: string
  packAssignmentId?: string
  userId?: string
  user_id?: string
  ownerId?: string
  owner_id?: string
  allocatedFrom?: string
  allocated_from?: string
  allocatedFromID?: string
  allocatedFromId?: string
  iccid?: string
  esimId?: string
  catalogId?: string
  catalogName?: string
  catalog_name?: string
  vendorPlanId?: string
  status?: string
  totalDataBytes?: number
  total_data_bytes?: number
  usedDataBytes?: number
  remainingDataBytes?: number
  remaining_data_bytes?: number
  dataInGB?: number
  total_data_in_gb?: number
  dataUsedInGB?: number
  dataRemainingInGB?: number
  packageName?: string
  packageDataInGB?: number
  packageValidityDays?: number
  validityInDays?: number
  remainingValidityInDays?: number
  expiresAt?: string
  usageAvailable?: boolean
  countries?: string[]
  createdAt?: string | number
  updatedAt?: string | number
  created_at?: string | number
  updated_at?: string | number
}

const normalizeRevampCountries = (countries?: string[] | null) => {
  const safeCountries = Array.isArray(countries) ? countries : []

  return safeCountries.map((countryCode) => ({
    countryName: countryCode,
    countryCode,
    networks: [],
    countryFlag: ''
  }))
}

const normalizeRevampPlanStatus = (status?: string) => {
  const normalized = String(status || '').toUpperCase()
  if (normalized === 'ACTIVE') return 'ACTIVE'
  if (normalized === 'ASSIGNED') return 'ACTIVE'
  if (normalized === 'TERMINATED') return 'TERMINATED'
  return 'INACTIVE'
}

const normalizeRevampPlanRow = (row: RevampPlanRow) => ({
  userPlanId: row.id || row.vendorPlanId || '',
  createdAt: row.createdAt || row.created_at || '',
  updatedAt: row.updatedAt || row.updated_at || '',
  userId: row.userId || row.user_id || '',
  iccid: row.iccid || row.esimId || '',
  vendor: '',
  catalogId: row.catalogId || '',
  appliedPlanId: row.vendorPlanId || row.id || '',
  planStatus: normalizeRevampPlanStatus(row.status),
  paymentIntent: '',
  packageName: row.catalogName || row.packageName || row.catalog_name || '',
  packageType: '',
  price: { currency: 'USD', value: '', symbol: '$' },
  supportedCountries: normalizeRevampCountries(row.countries),
  packageDataAllowed:
    row.dataInGB ||
    row.packageDataInGB ||
    row.total_data_in_gb ||
    ((row.totalDataBytes || row.total_data_bytes)
      ? (row.totalDataBytes || row.total_data_bytes || 0) / 1073741824
      : 0),
  packageValidityInDays: row.validityInDays || row.packageValidityDays || 0,
  dataUsageRemainingInBytes:
    row.remainingDataBytes ||
    row.remaining_data_bytes ||
    (typeof row.dataRemainingInGB === 'number'
      ? row.dataRemainingInGB * 1073741824
      : 0),
  packageExpiry: 0,
  allocatedFrom: row.allocatedFrom || row.allocated_from || '',
  allocatedFromID:
    row.ownerId ||
    row.owner_id ||
    row.allocatedFromID ||
    row.allocatedFromId ||
    '',
  packAssignmentId: row.packAssignmentId || row.id || ''
})

const normalizeRevampPlansResponse = <T extends GetUserPlansType | GetAllOtpackUserPlansType | GetActiveUserPlansType>(
  response: AxiosResponse<T>
) => {
  const responseData = response.data as T & {
    body?: { count?: number; offset?: number; total?: number; data?: RevampPlanRow[] }
    data?: RevampPlanRow[]
  }

  const rows = Array.isArray(responseData?.body?.data)
    ? responseData.body.data
    : Array.isArray(responseData?.data)
      ? responseData.data
    : []

  const safeRows = rows.filter(
    (row): row is RevampPlanRow => !!row && typeof row === 'object'
  )

  return {
    ...response,
    data: {
      ...response.data,
      body: {
        ...(responseData.body || {}),
        count: responseData.body?.count ?? safeRows.length,
        offset: responseData.body?.offset ?? 0,
        total: responseData.body?.total ?? safeRows.length,
        data: safeRows.map((row) => normalizeRevampPlanRow(row))
      }
    }
  }
}

class OneTimePackApi {
  static getAllFaberUsers(body: {
    searchCriteria: string
    searchValue: string
    faberToken: string
    limit: number
    planStatus: string
    offset: number
  }): AxiosPromise<GetAllFaberUsers> {
    let searchField = ''
    switch (body.searchCriteria) {
      case 'Email':
        searchField = 'email'
        break
      case 'MSISDN':
        searchField = 'msisdn'
        break
      case 'ICCID':
        searchField = 'iccid'
        break
      case 'Customer ID':
        searchField = 'userId'
        break
    }

    const searchValue = body.searchValue

    const requestBody = {
      [searchField]: searchValue,
      faberToken: body.faberToken,
      limit: body.limit,
      offset: body.offset,
      status: body.planStatus
    }

    return OrbitApi.httpPost(
      `/faber/user/search?&limit=${body.limit}&offset=${body.offset}&sort=desc`,
      {
        Authorization: `Bearer ${body.faberToken}`
      },
      requestBody
    )
  }

  static getAllInactiveOtpackUserPlans(
    userId: string,
    faberToken: string
  ): AxiosPromise<GetAllOtpackUserPlansType> {
    return FaberApi.httpGet(
      `/sim/revamp/ops/plans/user/${userId}?planStatuses=INACTIVE`,
      {
        Authorization: `Bearer ${faberToken}`
      },
      true
    ).then((response) => normalizeRevampPlansResponse(response))
  }

  static getExternalAllInactiveOtpackUserPlans(
    userId: string,
    faberToken: string
  ): AxiosPromise<GetAllOtpackUserPlansType> {
    return FaberApi.httpGet(
      `/sim/ext/plan/${userId}?planStatuses=INACTIVE`,
      {
        Authorization: `Bearer ${faberToken}`
      },
      true
    )
  }

  static getAllTerminatedOtpackUserPlans(
    userId: string,
    faberToken: string
  ): AxiosPromise<GetAllOtpackUserPlansType> {
    return FaberApi.httpGet(
      `/sim/revamp/ops/plans/user/${userId}?planStatuses=TERMINATED`,
      {
        Authorization: `Bearer ${faberToken}`
      },
      true
    ).then((response) => normalizeRevampPlansResponse(response))
  }

  static getExternalAllTerminatedOtpackUserPlans(
    userId: string,
    faberToken: string
  ): AxiosPromise<GetAllOtpackUserPlansType> {
    return FaberApi.httpGet(
      `/sim/ext/plan/${userId}?planStatuses=TERMINATED`,
      {
        Authorization: `Bearer ${faberToken}`
      },
      true
    )
  }

  static getAllActiveInactiveOtpackUserPlans(
    userId: string,
    faberToken: string,
    limit: number,
    offset: number
  ): AxiosPromise<GetUserPlansType> {
    return FaberApi.httpGet(
      `/sim/revamp/ops/plans/user/${userId}?planStatuses=ACTIVE,INACTIVE&offset=${offset}&limit=${limit}&orderBy=desc`,
      {
        Authorization: `Bearer ${faberToken}`
      },
      true
    ).then((response) => normalizeRevampPlansResponse(response))
  }

  static getAllActiveOtpackUserPlans(
    userId: string,
    faberToken: string
  ): AxiosPromise<GetActiveUserPlansType> {
    return FaberApi.httpGet(
      `/sim/revamp/ops/plans/user/${userId}/active`,
      {
        Authorization: `Bearer ${faberToken}`
      },
      true
    ).then((response) => normalizeRevampPlansResponse(response))
  }

  static getExternalAllActiveOtpackUserPlans(
    userId: string,
    faberToken: string
  ): AxiosPromise<GetActiveUserPlansType> {
    return FaberApi.httpGet(
      `/sim/revamp/ext/plans/user/${userId}/active`,
      {
        Authorization: `Bearer ${faberToken}`
      },
      true
    )
      .then((response) => normalizeRevampPlansResponse(response))
      .catch((error: AxiosError) => {
        if (error.response?.status && error.response.status !== 404) {
          throw error
        }

        return FaberApi.httpGet(
          `/sim/ext/plan/${userId}?planStatuses=ACTIVE`,
          {
            Authorization: `Bearer ${faberToken}`
          },
          true
        ).then((response) => normalizeRevampPlansResponse(response))
      })
  }

  static getActiveOtpUserPacks(
    userId: string,
    faberToken: string
  ): AxiosPromise<GetActiveUserPlansType> {
    return FaberApi.httpGet(
      `/sim/ops/plan/active/${userId}`,
      {
        Authorization: `Bearer ${faberToken}`
      },
      true
    )
  }

  static getSubscriptionUserPacks(
    userId: string,
    faberToken: string
  ): AxiosPromise<GetSubscriptionUserPlansType> {
    return FaberApi.httpGet(
      `/subscriptions?userID=${userId}`,
      { Authorization: `Bearer ${faberToken}` },
      true
    )
  }

  static getOtpackPlanHistory(
    userPlanId: string,
    faberToken: string
  ): AxiosPromise<GetOtpackHistoryType> {
    return FaberApi.httpGet(
      `/sim/ops/planhistory/${userPlanId}`,
      { Authorization: `Bearer ${faberToken}` },
      true
    )
  }

  static getAllUserPlans(
    faberToken: string
  ): AxiosPromise<GetPlansDetailsType> {
    return FaberApi.httpGet(
      '/sim/ops/plan/count',
      {
        Authorization: `Bearer ${faberToken}`
      },
      true
    )
  }

  static createUserPlan(body: {
    userId: string
    catalogId: string
    paymentIntent: string
    faberToken: string
  }): AxiosPromise<CreateUserPlanResponse> {
    return FaberApi.httpPost(
      `/sim/ops/esim/package/provision/${body.userId}`,
      {
        Authorization: `Bearer ${body.faberToken}`
      },
      body
    )
  }

  static createUserPlanByICCID(body: {
    userId: string
    catalogId: string
    paymentIntent: string
    faberToken: string
    iccid: string
  }): AxiosPromise<CreateUserPlanResponse> {
    const { faberToken, ...payload } = body
    return FaberApi.httpPost(
      `/sim/revamp/ops/esim/package/provision/iccid/${encodeURIComponent(body.userId)}`,
      {
        Authorization: `Bearer ${faberToken}`
      },
      payload
    )
  }

  static editUserDetails(body: {
    userId: string
    detailCategory: string
    newValue: string
    faberToken: string
  }): AxiosPromise<EditUserPlanDetailsResponse> {
    return OrbitApi.httpPatch(
      `/faber/user/details/${body.userId}`,
      {
        Authorization: `Bearer ${body.faberToken}`
      },
      body
    )
  }

  static getUserPlanByPaymentId(
    userId: string,
    paymentId: string,
    faberToken: string
  ): AxiosPromise<UserPlanByPaymentIdType> {
    return FaberApi.httpGet(
      `/sim/ops/plan/user/${userId}/payment/${paymentId}`,
      {
        Authorization: `Bearer ${faberToken}`
      },
      true
    )
  }
}

export default OneTimePackApi
